// src/components/ActiveSignalsTicker.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { db } from '../firebase';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';

function fmtPrice(val) {
  if (val == null || val === '') return '-';
  const n = Number(val);
  if (isNaN(n)) return '-';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(4);
  if (n >= 0.0001) return n.toFixed(6);
  return n.toPrecision(4);
}

function getBaseSymbol(sig) {
  const raw = (sig.symbol || sig.coinId || '').toString().trim().toUpperCase();
  if (!raw) return '';
  return raw.replace(/[^A-Z0-9]/g, '');
}

function calcActiveGain(sig, livePrice) {
  const entry = parseFloat(sig.entryPrice || sig.averageEntryPrice);
  const live = parseFloat(livePrice);
  if (!entry || !live || isNaN(entry) || isNaN(live)) return null;
  const isShort = (sig.signalType || '').toUpperCase() === 'SHORT';
  return isShort ? ((entry - live) / entry) * 100 : ((live - entry) / entry) * 100;
}

const ActiveSignalsTicker = () => {
  const [signals, setSignals] = useState([]);
  const [livePrices, setLivePrices] = useState({});
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'signals'), where('status', 'in', ['ACTIVE', 'TP1 HIT']), limit(80));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const ta = a?.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a?.createdAt || 0).getTime();
          const tb = b?.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b?.createdAt || 0).getTime();
          return tb - ta;
        });
        setSignals(list.slice(0, 40));
      },
      () => {}
    );

    return () => unsub();
  }, []);

  const pairs = useMemo(() => {
    return [...new Set(signals.map((s) => getBaseSymbol(s)).filter(Boolean).map((base) => `${base}USDT`))];
  }, [signals]);

  useEffect(() => {
    if (pairs.length === 0) {
      setLivePrices({});
      return;
    }

    let mounted = true;

    const fetchLivePrices = async () => {
      try {
        const endpoint =
          pairs.length === 1
            ? `https://api.binance.com/api/v3/ticker/price?symbol=${encodeURIComponent(pairs[0])}`
            : `https://api.binance.com/api/v3/ticker/price?symbols=${encodeURIComponent(JSON.stringify(pairs))}`;

        const res = await fetch(endpoint);
        if (!res.ok) return;

        const payload = await res.json();
        const rows = Array.isArray(payload) ? payload : [payload];
        const map = {};

        rows.forEach((row) => {
          const pair = (row?.symbol || '').toUpperCase();
          const price = Number(row?.price);
          if (!pair.endsWith('USDT') || !Number.isFinite(price)) return;
          const base = pair.replace(/USDT$/, '');
          map[base] = price;
        });

        if (mounted) setLivePrices(map);
      } catch {
        // keep prior snapshot
      }
    };

    fetchLivePrices();
    const id = setInterval(fetchLivePrices, 15000);

    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [pairs]);

  if (signals.length === 0) return null;

  const items = [...signals, ...signals];

  return (
    <TickerBar onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <TickerLabel>
        <LabelDot />
        Active Signals
      </TickerLabel>

      <TickerClip>
        <TickerTrack ref={trackRef} paused={paused} count={signals.length}>
          {items.map((sig, idx) => {
            const base = getBaseSymbol(sig);
            const livePrice = base ? livePrices[base] : null;
            const gain = calcActiveGain(sig, livePrice);
            const isLong = (sig.signalType || '').toUpperCase() !== 'SHORT';
            const isTp1Hit = (sig.status || '').toUpperCase().includes('TP1');
            const chipLabel = isTp1Hit
              ? `TP1 HIT LEADING TO TP2 (${fmtPrice(sig.takeProfit)})`
              : 'ACTIVE';

            return (
              <React.Fragment key={`${sig.id}-${idx}`}>
                <TickerItem>
                  <Coin>{(sig.symbol || sig.coinId || '?').toUpperCase()}</Coin>
                  <DirBadge long={isLong}>{isLong ? 'LONG' : 'SHORT'}</DirBadge>

                  <PriceGroup>
                    <EntryLabel>Entry</EntryLabel>
                    <EntryVal>{fmtPrice(sig.entryPrice || sig.averageEntryPrice)}</EntryVal>
                  </PriceGroup>

                  <Arrow win={gain > 0} loss={gain < 0}>
                    {gain > 0 ? '↑' : gain < 0 ? '↓' : '→'}
                  </Arrow>

                  <PriceGroup>
                    <HitLabel>Now</HitLabel>
                    <HitVal>{fmtPrice(livePrice)}</HitVal>
                  </PriceGroup>

                  {gain !== null && (
                    <GainVal win={gain > 0} loss={gain < 0}>
                      {gain > 0 ? '+' : ''}
                      {gain.toFixed(2)}%
                    </GainVal>
                  )}

                  <ResultChip neutral>{chipLabel}</ResultChip>
                </TickerItem>
                {idx < items.length - 1 && (
                  <SeparatorWrap>
                    <SeparatorLogo src="/logo.png" alt="Trend Genie" />
                  </SeparatorWrap>
                )}
              </React.Fragment>
            );
          })}
        </TickerTrack>
      </TickerClip>
    </TickerBar>
  );
};

export default ActiveSignalsTicker;

const marquee = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

const TickerBar = styled.div`
  width: 100%;
  background: var(--bg-surface);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  overflow: hidden;
  height: 44px;
  position: relative;
`;

const TickerLabel = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 1rem 0 1.2rem;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--text-muted);
  border-right: 1px solid var(--border-color);
  height: 100%;
  background: var(--bg-secondary);
  white-space: nowrap;
`;

const LabelDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #29abe2;
  flex-shrink: 0;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const TickerClip = styled.div`
  flex: 1;
  overflow: hidden;
  height: 100%;
  display: flex;
  align-items: center;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 48px;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, var(--bg-surface), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, var(--bg-surface), transparent);
  }
`;

const TickerTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  white-space: nowrap;
  will-change: transform;
  animation: ${marquee} ${(p) => Math.max(p.count * 3.5, 30)}s linear infinite;
  animation-play-state: ${(p) => (p.paused ? 'paused' : 'running')};
`;

const TickerItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0 0.9rem;
`;

const SeparatorWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.5rem;
`;

const Coin = styled.span`
  font-size: 0.82rem;
  font-weight: 800;
  color: var(--text-color);
  letter-spacing: -0.2px;
`;

const DirBadge = styled.span`
  font-size: 0.6rem;
  font-weight: 800;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  letter-spacing: 0.05em;
  background: ${(p) => (p.long ? 'rgba(41,171,226,0.12)' : 'rgba(239,68,68,0.1)')};
  color: ${(p) => (p.long ? '#29ABE2' : '#ef4444')};
  border: 1px solid ${(p) => (p.long ? 'rgba(41,171,226,0.25)' : 'rgba(239,68,68,0.2)')};
`;

const PriceGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const EntryLabel = styled.span`
  font-size: 0.68rem;
  color: var(--text-muted);
`;

const EntryVal = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--text-secondary-color);
`;

const Arrow = styled.span`
  font-size: 0.7rem;
  color: ${(p) => (p.win ? '#10b981' : p.loss ? '#ef4444' : 'var(--text-muted)')};
  flex-shrink: 0;
`;

const HitLabel = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--text-muted);
`;

const HitVal = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--text-secondary-color);
`;

const GainVal = styled.span`
  font-size: 0.78rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.win ? '#10b981' : p.loss ? '#ef4444' : 'var(--text-muted)')};
`;

const ResultChip = styled.span`
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  background: rgba(107, 114, 128, 0.1);
  color: #9ca3af;
`;

const SeparatorLogo = styled.img`
  display: block;
  align-self: center;
  width: 22px;
  height: 22px;
  object-fit: contain;
  opacity: 0.95;
`;
